import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListCardSkeleton } from '../Common/PageSkeleton';
import { useNotifications } from '../../context/NotificationContext';
import { ArrowLeft, Bell, CheckCheck, RefreshCw } from 'lucide-react';
import './NotificationsPage.css';

const formatFullDate = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState('all'); // 'all', 'orders', 'refunds'

  const filteredNotifications = notifications.filter(n => {
    const type = (n.type || n.data?.type || '').toLowerCase();
    if (filter === 'orders') return type.includes('order');
    if (filter === 'refunds') return type.includes('refund') || type.includes('wallet');
    return true;
  });

  const handleNotificationClick = (notif) => {
    markAsRead(notif.id || notif._id);
    const type = (notif.type || notif.data?.type || '').toLowerCase();
    const orderId = notif.data?.orderId || notif.orderId;

    if (type.includes('refund') || type.includes('wallet')) {
      navigate('/refunds');
    } else if (orderId) {
      navigate(`/order-details/${orderId}`);
    } else if (type.includes('order')) {
      navigate('/order-tracking');
    }
  };

  return (
    <div className="notif-page-container">
      {/* Top Header */}
      <div className="notif-page-header">
        <div className="notif-header-left">
          <button className="notif-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="notif-page-title">Notifications</h1>
            <p className="notif-page-subtitle">Real-time alerts for orders, refunds & wallet</p>
          </div>
        </div>

        <div className="notif-header-actions">
          <button className="notif-action-icon-btn" onClick={fetchNotifications} title="Refresh">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>

          {unreadCount > 0 && (
            <button className="notif-mark-all-page-btn" onClick={markAllAsRead}>
              <CheckCheck size={16} /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="notif-tabs-bar">
        <button
          className={`notif-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Notifications ({notifications.length})
        </button>
        <button
          className={`notif-tab ${filter === 'orders' ? 'active' : ''}`}
          onClick={() => setFilter('orders')}
        >
          🛍️ Order Updates
        </button>
        <button
          className={`notif-tab ${filter === 'refunds' ? 'active' : ''}`}
          onClick={() => setFilter('refunds')}
        >
          💰 Refunds & Wallet
        </button>
      </div>

      {/* Content Section */}
      <div className="notif-content-area">
        {loading && notifications.length === 0 ? (
          <ListCardSkeleton count={4} />
        ) : filteredNotifications.length === 0 ? (

          <div className="notif-page-empty">
            <div className="notif-page-empty-icon">🔔</div>
            <h3>No Notifications Found</h3>
            <p>You have no notifications matching this category.</p>
          </div>
        ) : (
          <div className="notif-page-card-list">
            {filteredNotifications.map((notif, idx) => {
              const notifId = notif.id || notif._id || idx;
              const isUnread = !notif.read;
              const type = (notif.type || '').toLowerCase();

              let icon = '🔔';
              let badgeColor = '#fef3c7';
              if (type.includes('refund') || type.includes('wallet')) {
                icon = '💰';
                badgeColor = '#e6f4ea';
              } else if (type.includes('order')) {
                icon = '🛍️';
                badgeColor = '#e8f0fe';
              }

              return (
                <div
                  key={notifId}
                  className={`notif-page-card ${isUnread ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="notif-card-icon" style={{ backgroundColor: badgeColor }}>
                    {icon}
                  </div>

                  <div className="notif-card-body">
                    <div className="notif-card-top flex justify-between items-center">
                      <span className="notif-card-title">{notif.title}</span>
                      {isUnread && <span className="notif-new-badge">NEW</span>}
                    </div>

                    <p className="notif-card-msg">{notif.message}</p>
                    <span className="notif-card-time">{formatFullDate(notif.timestamp || notif.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
